/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   3/24/22
 */

import React from "react";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
} from "@mui/material";
import DateFilterControl from "../../controls/DateFilterControl";
import EventTypeControl from "../../controls/EventTypeControl";
import BooleanControl from "../../controls/BooleanControl";
import EmployeeFilterControl from "./EmployeeFilterControl";
import Const from "../../const.json";
import LaunchIcon from '@mui/icons-material/Launch';

export interface FilterToolbarProps {
	filters: Record<string, any>
	setFilters: React.Dispatch<React.SetStateAction<Record<string, any>>>
}

export default function FilterToolbar(props: FilterToolbarProps) {

	return (
		<Stack direction={"row"} justifyContent={"space-between"} spacing={1} sx={{ mb: 1 }}>
			<FilterDialog
				{...props}
			/>
			<Button
				variant={"outlined"}
				href={Const.URL_ADV_REPORTING}
				target={"_blank"}
				startIcon={<LaunchIcon />}
			>
				Advanced Reporting
			</Button>
		</Stack>
	);
}


export function FilterDialog({setFilters: setFilterVars} : FilterToolbarProps) {

	const [isDialogOpen, setIsDialogOpen] = React.useState(false);
	const [filters, setFilters] = React.useState<Record<string, Record<string, any>>>({});
	const [filtersApplied, setFiltersApplied] = React.useState(0);

	const updateFilters = (filters: Record<string, any>) => {
		setFilters(f => ({
			...f,
			...filters
		}));
	}

	const onFilterClear = (f: string) => {
		const {[f]: omit, ...rest} = filters;
		setFilters(rest);
	}

	const onClose = () => setIsDialogOpen(false);

	return (
		<>
			<Button
				variant={"outlined"}
				onClick={() => setIsDialogOpen(true)}
			>
				{filtersApplied === 0 && "Set filter"}
				{filtersApplied > 0 && filtersApplied + " filter" + (filtersApplied === 1 ? "" : "s") +" applied"}
			</Button>
			<Dialog
				open={isDialogOpen}
				onClose={onClose}
				maxWidth={"md"}
				fullWidth
			>
				<DialogTitle>Set Filter</DialogTitle>
				<DialogContent>
					<Stack spacing={1}>
						<DateFilterControl
							label={"Creation Date"}
							range={filters.creationDate ? {
								start: filters.creationDate.creationDateGte,
								end: filters.creationDate.creationDateLte,
							} : undefined}
							onChange={(range) => {
								if (range) {
									updateFilters({
										creationDate: {
											creationDateGte: range.start,
											creationDateLte: range.end
										}
									});
								} else {
									onFilterClear("creationDate");
								}
							}}
						/>
						<EmployeeFilterControl
							label={"Created By"}
							attribute={"createdByIn"}
							values={filters.createdBy?.createdByIn ?? []}
							onChange={(filter) => {
								updateFilters({ createdBy: filter });
							}}
							onClear={() => {
								onFilterClear("createdBy");
							}}
						/>
						<EventTypeControl
							label={"Type"}
							showTypes={true}
							showCategories={false}
							values={filters.typeId?.typeIdIn || []}
							onChange={(types) => {
								if (types.length > 0) updateFilters({ typeId: { typeIdIn: types } });
								else onFilterClear("typeId");
							}}
						/>
						<EventTypeControl
							label={"Category"}
							showTypes={false}
							showCategories={true}
							values={filters.typeParentId?.typeParentIdIn || []}
							onChange={(types) => {
								if (types.length > 0) updateFilters({ typeParentId: { typeParentIdIn: types } });
								else onFilterClear("typeParentIdIn");
							}}
						/>
						<DateFilterControl
							label={"Event Date"}
							range={filters.eventDate ? {
								start: filters.eventDate.endDatetimeGt,
								end: filters.eventDate.startDatetimeLt,
							} : undefined}
							onChange={(range) => {
								if (range) {
									updateFilters({
										eventDate: {
											endDatetimeGt: range.start,
											startDatetimeLt: range.end
										}
									});
								} else {
									onFilterClear("eventDate");
								}
							}}
						/>
						<EmployeeFilterControl
							label={"Employee"}
							attribute={"employeeIdIn"}
							values={filters.employeeIdIn?.employeeIdIn ?? []}
							onChange={(filter) => {
								updateFilters({ employeeIdIn: filter });
							}}
							onClear={() => {
								onFilterClear("employeeIdIn");
							}}
						/>
						<EmployeeFilterControl
							label={"Manager"}
							attribute={"managerIdIn"}
							values={filters.managerIdIn?.managerIdIn ?? []}
							onChange={(filter) => {
								updateFilters({ managerIdIn: filter });
							}}
							onClear={() => {
								onFilterClear("managerIdIn");
							}}
						/>
						<BooleanControl
							label={"Confirmed"}
							value={filters.confirmed?.isConfirmed}
							onChange={(value) => {
								if (value !== null) updateFilters({ confirmed: { isConfirmed: value } });
								else onFilterClear("confirmed");
							}}
						/>
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button onClick={onClose}>Cancel</Button>
					<Button onClick={() => {
						setFilters({});
						setFilterVars({});
						setFiltersApplied(0);
						onClose();
					}}>Reset</Button>
					<Button variant={"contained"} onClick={() => {
						let filter: Record<string, any> = {};
						Object.keys(filters).forEach(f => filter = { ...filter, ...filters[f] })
						setFilterVars(filter);
						setFiltersApplied(Object.keys(filters).length);
						onClose();
					}}>Save</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}